import './style.scss';

import { Wallet } from '@ethersproject/wallet';
import { useCallback, useContext, useState } from 'react';
import { tailwindConfig } from 'tailwind-config';

import { ApiPairs, ApiWallets } from 'api';
import { PairWalletsContext } from 'context/PairWalletsContext/PairWalletsContext';
import { Checkbox } from 'shared/components';
import { useTypedDispatch, useTypedSelector } from 'store';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { pairBots } from 'types/bots';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { Modal } from 'ui/Modal/Modal';
import { shortenAddress } from 'utils';
import { readFromClipboard } from 'utils/clipboard';

import { TrashIcon } from 'assets/icons';

type WalletsImportModalProps = {
  isVisibleBots?: boolean;
  onClose: () => void;
};

const isLikelyAddress = (str: string) => /^0x[a-fA-F0-9]{40}$/.test(str);
const normalizePrivateKey = (key: string) => key.replace(/^0x/i, '');
const isLikelyPrivateKey = (key: string) => /^[0-9a-fA-F]{64}$/.test(normalizePrivateKey(key));

export const WalletsImportModal = ({ isVisibleBots, onClose }: WalletsImportModalProps) => {
  const pair = useTypedSelector(store => store.pairs.selectedDexPair);
  const dispatch = useTypedDispatch();

  const { handleRemount } = useContext(PairWalletsContext);

  const [wallets, setWallets] = useState<{ address: string; private_key: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [edited, setEdited] = useState<boolean>(false);
  const [checkedBots, setCheckedBots] = useState<number[]>([]);

  const handlePasteWallets = useCallback(async () => {
    try {
      const text = await readFromClipboard();

      if (!text) {
        setEdited(false);
        return;
      }

      setEdited(true);

      const normalized = text
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/;/g, ' ')
        .replace(/ +/g, ' ')
        .trim();

      const tokens = normalized
        .split(' ')
        .map(el => el.trim())
        .filter(el => el.length > 0);

      const existingKeys = new Set(wallets.map(w => w.private_key));
      const result: { address: string; private_key: string }[] = [];

      let foundSomething = false;
      let duplicateFound = false;

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];

        if (isLikelyAddress(token)) {
          foundSomething = true;
          const next = tokens[i + 1];

          if (next && isLikelyPrivateKey(next)) {
            const privateKey = normalizePrivateKey(next);

            if (existingKeys.has(privateKey)) {
              duplicateFound = true;
            } else {
              result.push({ address: token, private_key: privateKey });
              existingKeys.add(privateKey);
            }
            i++;
          }
        } else if (isLikelyPrivateKey(token)) {
          foundSomething = true;
          const privateKey = normalizePrivateKey(token);

          if (existingKeys.has(privateKey)) {
            duplicateFound = true;
          } else {
            try {
              const wallet = new Wallet(privateKey);
              result.push({ address: wallet.address, private_key: privateKey });
              existingKeys.add(privateKey);
            } catch (err) {
              console.error(err);
            }
          }
        }
      }

      if (!foundSomething) {
        dispatch(
          setAlertState({
            type: 'failed-img',
            text: 'No valid addresses or private keys found in the pasted text',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => dispatch(dropAlertState()),
          }),
        );
        return;
      }

      if (result.length > 0) {
        setWallets(prev => [...prev, ...result]);
      }

      if (result.length === 0) {
        dispatch(
          setAlertState({
            type: 'failed-img',
            text: 'Private keys found but failed to parse or associate with valid addresses',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => dispatch(dropAlertState()),
          }),
        );
      }

      if (duplicateFound) {
        dispatch(
          setAlertState({
            type: 'failed-img',
            text: 'Some private keys are already in the list and were not added again',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => dispatch(dropAlertState()),
          }),
        );
      }
    } catch (error) {
      dispatch(
        setAlertState({
          type: 'failed-img',
          text: 'An error occurred while processing clipboard data',
          onClose: () => dispatch(dropAlertState()),
          onSubmit: () => dispatch(dropAlertState()),
        }),
      );
    }
  }, [dispatch, wallets]);

  const handleDeleteWallet = useCallback((private_key: string) => {
    setWallets(v => [...v].filter(el => el.private_key !== private_key));
  }, []);

  const handleImport = useCallback(async () => {
    try {
      setLoading(true);

      const { errorMessage, isSuccess } =
        isVisibleBots && pair
          ? await ApiPairs.walletsImport({
              pairId: pair.id,
              botsIds: checkedBots,
              wallets,
            })
          : await ApiWallets.walletsImport({
              wallets,
            });

      if (isSuccess) {
        if (isVisibleBots && pair) {
          handleRemount();
        } else {
          location.reload();
        }

        onClose();
        dispatch(
          setAlertState({
            type: 'success-img',
            text: 'All wallets were imported successfully',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => dispatch(dropAlertState()),
          }),
        );
      } else {
        dispatch(
          setAlertState({
            type: 'failed-img',
            text: errorMessage.includes('wallets_address_uindex')
              ? 'Import error, these wallets may have already been imported'
              : errorMessage,
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
            },
          }),
        );
      }
    } catch (error) {
      dispatch(
        setAlertState({
          type: 'failed-img',
          text: 'Failed to import...',
          onClose: () => dispatch(dropAlertState()),
          onSubmit: () => {
            dispatch(dropAlertState());
          },
        }),
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, wallets, pair, onClose]);

  return (
    <Modal onClose={onClose} title="Import wallets" edited={edited}>
      {isVisibleBots && (
        <div className="mb-6">
          <Checkbox.Group
            options={pairBots.map(bot => ({
              label: bot.description,
              value: bot.id,
            }))}
            value={checkedBots}
            onChange={setCheckedBots}
          />
        </div>
      )}

      <div className="ui-wallets-import">
        {wallets.length === 0 && (
          <div className="no-wallets">
            <span>Add wallets for import</span>
          </div>
        )}

        {wallets.length !== 0 && (
          <div className="wallets-list scrollable">
            {wallets.map((el, index) => (
              <div className="wallet-item" key={el.private_key}>
                <div className="wallet-item__left">
                  <div className="index">#{index + 1}</div>

                  <div className="flex flex-col gap-0.5">
                    <div className="wallets-address">
                      <span>Wallet</span>
                      <strong>{el.address ? shortenAddress(el.address) : '-'}</strong>
                    </div>

                    <div className="wallets-key">
                      <span>Private key</span>
                      <strong>{shortenAddress(el.private_key)}</strong>
                    </div>
                  </div>
                </div>

                <button
                  className="delete-wallet-item"
                  onClick={() => handleDeleteWallet(el.private_key)}
                >
                  <TrashIcon color={tailwindConfig.theme.colors.primary} />
                </button>
              </div>
            ))}
          </div>
        )}

        <button className="button-paste-wallets" onClick={handlePasteWallets}>
          + Paste wallets
        </button>

        {loading && <ButtonLoading />}

        {!loading && (
          <Button disabled={wallets.length === 0} onClick={handleImport}>
            Import
          </Button>
        )}
      </div>
    </Modal>
  );
};
